import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import css from './Beacon.module.css';
import { findVisible } from './dom';
import { hasSeen, markSeen } from './seen';
import { SparkleIcon } from './SparkleIcon';
import {
  POINTER_CLOSED_EVENT,
  POINTER_TOGGLE_EVENT,
  type PointerAnchor,
} from './open-event';
import type { PointerHelpSource } from './types';

const SHOW_DELAY_MS = 500;
const PIN_INSET = 14;

function getPinPoint(element: HTMLElement, corner: 'top-right' | 'top-left') {
  if (corner === 'top-left') {
    const range = document.createRange();
    range.selectNodeContents(element);
    const text = range.getBoundingClientRect();
    return { top: text.top - 2, left: text.left - 4 };
  }
  const rect = element.getBoundingClientRect();
  return { top: rect.top + PIN_INSET, left: rect.right - PIN_INSET };
}

export function Beacon({
  target,
  help,
  active,
  align = 'end',
  pinColor = '#fff',
  openBelowPin = false,
  pinCorner = 'top-right',
  id,
}: {
  id: string;
  target: string;
  help: PointerHelpSource;
  active: boolean;
  align?: 'start' | 'end';
  pinColor?: string;
  openBelowPin?: boolean;
  pinCorner?: 'top-right' | 'top-left';
}) {
  const [point, setPoint] = useState<{ top: number; left: number } | null>(
    null,
  );

  const helpRef = useRef(help);
  helpRef.current = help;

  useEffect(() => {
    if (!active || hasSeen('beacon', id)) return undefined;
    let element: HTMLElement | undefined;
    let observer: ResizeObserver | undefined;
    let isDismissed = false;

    let frame = 0;
    const update = () => {
      frame = 0;
      if (!element || isDismissed) return;
      const next = getPinPoint(element, pinCorner);
      setPoint((current) =>
        current && current.top === next.top && current.left === next.left
          ? current
          : next,
      );
    };
    const measure = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const hide = () => {
      markSeen('beacon', id);
      isDismissed = true;
      setPoint(null);
    };

    const timer = setTimeout(() => {
      element = findVisible(target);
      if (!element) return;
      update();
      observer = new ResizeObserver(measure);
      observer.observe(element);
      window.addEventListener('scroll', measure, true);
      window.addEventListener('resize', measure);
      element.addEventListener('click', hide);
      window.addEventListener(POINTER_CLOSED_EVENT, hide);

      const rect = element.getBoundingClientRect();
      const pin = getPinPoint(element, pinCorner);
      let anchor: PointerAnchor;
      if (openBelowPin && pinCorner === 'top-left') {
        anchor = {
          help: helpRef.current,
          isAutomatic: true,
          align: 'end',
          x: pin.left + 8,
          y: pin.top + 6,
          originX: pin.left,
          originY: pin.top,
        };
      } else if (openBelowPin) {
        anchor = {
          help: helpRef.current,
          isAutomatic: true,
          align: 'end',
          x: pin.left + PIN_INSET,
          y: pin.top,
          originX: pin.left,
          originY: pin.top,
        };
      } else {
        anchor = {
          help: helpRef.current,
          isAutomatic: true,
          align,
          x: align === 'end' ? rect.right : rect.left,
          y: rect.bottom - 8,
          originX: rect.left + rect.width / 2,
          originY: rect.top + rect.height / 2,
        };
      }
      window.dispatchEvent(
        new CustomEvent<PointerAnchor>(POINTER_TOGGLE_EVENT, {
          detail: anchor,
        }),
      );
    }, SHOW_DELAY_MS);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      element?.removeEventListener('click', hide);
      window.removeEventListener(POINTER_CLOSED_EVENT, hide);
      setPoint(null);
    };
  }, [active, id, target, align, openBelowPin, pinCorner]);

  if (!point) return null;
  return createPortal(
    <SparkleIcon
      className={css.pin}
      style={{ top: point.top, left: point.left, color: pinColor }}
    />,
    document.body,
  );
}
